import React, { useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../../../../services/apiService';
import '../report.css';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../../assets/images/Pro_Logo.png';
const XReceipt = () => {
  const [employees, setEmployees] = useState([]);
  const [isEmpLoading, setIsEmpLoading] = useState(false);
  const [empError, setEmpError] = useState('');
  const navigate = useNavigate();
  const [groupedData, setGroupedData] = useState({});
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);

  const printRef = useRef();

  const [formData, setFormData] = useState({
    ReportType: null, // { label, value: userId, data: originalRow }
  });

  // --- Fetch employees for dropdown ---
  useEffect(() => {
    const fetchEmployees = async () => {
      setIsEmpLoading(true);
      setEmpError('');
      try {
        const apiService = new ApiService();

        const res = await apiService.get('GET_EMPLOYEE');
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];

        setEmployees(list);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setEmpError('Failed to load employees. Please try again.');
      } finally {
        setIsEmpLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Build dropdown options from employees
  const employeeOptions = useMemo(() => {
    return employees.map((e) => ({
      label: `${e?.employee_Name ?? 'Unknown'}${e?.branchName ? ' — ' + e.branchName : ''}`,
      value: e?.userId ?? '', // this is what GET_X_REPORT expects as "UserId"
      data: e,
    }));
  }, [employees]);

  const handleReportType = (selectedOption) => {
    setFormData((prev) => ({ ...prev, ReportType: selectedOption }));
  };

  // --- Fetch X Report for selected user ---
  const getReportData = async () => {
    setReportError('');
    setGroupedData({});
    if (!formData?.ReportType?.value) {
      setReportError('Please select a user first.');
      return;
    }

    setIsReportLoading(true);
    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();
      // backend expects "UserId"
      params.append('UserId', String(formData.ReportType.value).trim());
      params.append('ShiftOpeningDate', Date.now()); // required but ignored by backend
      const res = await apiService.getApiParamater('GET_X_REPORT', `?${params.toString()}`);

      const rows = Array.isArray(res?.data?.result) ? res.data.result : [];
      if (!rows.length) {
        setReportError('No report data found for the selected user.');
        return;
      }

      // Group by "section" like before
      const grouped = rows.reduce((acc, curr) => {
        const key = curr?.section || 'UNSPECIFIED';
        if (!acc[key]) acc[key] = [];
        acc[key].push(curr);
        return acc;
      }, {});

      setGroupedData(grouped);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching X Report:', error);
      setReportError('Failed to fetch report. Please try again.');
    } finally {
      setIsReportLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!printRef.current) return;

    const canvas = await html2canvas(printRef.current, { scale: 3 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'px',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = position - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save('X-Report.pdf');
  };

  // Order sections: ensure "REPORT INFO" first if present
  const orderedSections = useMemo(() => {
    const keys = Object.keys(groupedData);
    if (!keys.length) return [];
    const rest = keys.filter((k) => k !== 'REPORT INFO').sort();
    return ['REPORT INFO', ...rest.filter((k) => k in groupedData)];
  }, [groupedData]);

  // react-select styles
  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': {
          backgroundColor: '#F5F5F5',
          color: '#000',
        },
      }),
    }),
    [],
  );

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <div className="row">
        <div className="col-3">
          <label className="form-label">Users</label>
          <Select
            isClearable
            isLoading={isEmpLoading}
            menuPortalTarget={document.body}
            styles={selectStyles}
            value={formData?.ReportType}
            options={employeeOptions}
            onChange={handleReportType}
            placeholder={isEmpLoading ? 'Loading users…' : 'Select User'}
            noOptionsMessage={() => (empError ? 'Error loading users' : 'No employees found')}
          />
          {empError ? (
            <div className="text-danger mt-2" style={{ fontSize: 12 }}>
              {empError}
            </div>
          ) : null}
        </div>

        <div className="col-3 justify-content-center align-items-center d-flex">
          <label className="form-label">{''}</label>
          <button className="refbtn center w-50" onClick={getReportData} disabled={!formData?.ReportType?.value || isReportLoading}>
            {isReportLoading ? 'Loading…' : 'Search'}
          </button>
          {reportError ? (
            <div className="text-danger mt-2" style={{ fontSize: 12 }}>
              {reportError}
            </div>
          ) : null}
        </div>
      </div>

      <Modal show={isModalVisible} onHide={() => setIsModalVisible(false)} centered>
        <Modal.Header closeButton>
          <div className="receipt-logo center">X Report</div>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef}>
            <div className="receipt-container">
              <div className="receipt-header">
                {/* <div className="receipt-logo">Logo</div> */}
                <div className="receipt-logo">
                  <img src={Logo} alt="Logo" style={{ maxWidth: '150px', maxHeight: '80px' }} />
                </div>
                <div className="receipt-date">{new Date().toLocaleDateString('en-GB')}</div>
              </div>

              {orderedSections.map((sectionName) => (
                <div key={sectionName} className="section-block">
                  <div className="section-title">{sectionName.toUpperCase()}</div>
                  <hr />
                  {groupedData[sectionName]?.map((item, index) => {
                    const label = item?.detail || item?.category || '-';
                    const amountNumber = Number(item?.amount === null || item?.amount === undefined ? 0 : item.amount);
                    const amount =
                      sectionName === 'REPORT INFO'
                        ? ''
                        : amountNumber.toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });
                    return (
                      <div key={index} className="receipt-row">
                        <span>{label}</span>
                        {sectionName === 'REPORT INFO' ? '' : <span>{amount}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}

              {!orderedSections.length && <div className="text-muted">No data to display.</div>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="center w-100">
            <button className="refbtn w-100 center" onClick={handlePrint}>
              Print PDF
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default XReceipt;
